import {CheckCircleIcon} from "@heroicons/react/20/solid";
import {
  PasswordRuleResult,
  PasswordStrength,
  passwordStrengthLabels,
  scorePasswordStrength
} from "./password-policy";

const fillClasses: Record<PasswordStrength, string> = {
  0: "bg-[var(--ots-outline-variant)]",
  1: "bg-[var(--ots-accent)]",
  2: "bg-[var(--ots-caution)]",
  3: "bg-[var(--ots-success)]",
  4: "bg-[var(--ots-success)]"
};

const labelClasses: Record<PasswordStrength, string> = {
  0: "text-[var(--ots-text-muted)]",
  1: "text-[var(--ots-error)]",
  2: "text-[var(--ots-warning)]",
  3: "text-[var(--ots-success)]",
  4: "text-[var(--ots-success)]"
};

export type PasswordStrengthMeterProps = {
  password: string;
  minLength?: number;
  className?: string;
};

/** Four-step strength bar with a spoken label. Advisory only; the policy decides what is allowed. */
export const PasswordStrengthMeter = ({password, minLength, className}: PasswordStrengthMeterProps) => {
  const strength = scorePasswordStrength(password, minLength);

  return (
    <div className={["flex items-center gap-3", className].filter(Boolean).join(" ")}>
      <div aria-hidden="true" className="grid flex-1 grid-cols-4 gap-1">
        {[1, 2, 3, 4].map((step) => (
          <span
            key={step}
            className={[
              "h-1.5 rounded-full transition-colors",
              password && strength >= step ? fillClasses[strength] : fillClasses[0]
            ].join(" ")}/>
        ))}
      </div>
      <span aria-live="polite" className={["min-w-16 shrink-0 text-right text-xs font-semibold", labelClasses[strength]].join(" ")}>
        {password && (
          <>
            <span className="sr-only">Password strength: </span>
            {passwordStrengthLabels[strength]}
          </>
        )}
      </span>
    </div>
  );
};

export type PasswordRuleChecklistProps = {
  rules: PasswordRuleResult[];
  className?: string;
};

/** Requirements that tick off as they are met, so nobody learns the rules by failing them. */
export const PasswordRuleChecklist = ({rules, className}: PasswordRuleChecklistProps) => (
  <ul aria-label="Password requirements" className={["grid gap-1.5 text-xs sm:grid-cols-2", className].filter(Boolean).join(" ")}>
    {rules.map((rule) => (
      <li
        key={rule.id}
        className={["flex items-center gap-1.5", rule.met ? "text-[var(--ots-success)]" : "text-[var(--ots-text-muted)]"].join(" ")}>
        {rule.met
          ? <CheckCircleIcon aria-hidden="true" className="size-4 shrink-0"/>
          : <span aria-hidden="true" className="mx-[3px] size-2.5 shrink-0 rounded-full border-[1.5px] border-current"/>}
        <span>
          {rule.label}
          <span className="sr-only">{rule.met ? " (met)" : " (not met)"}</span>
        </span>
      </li>
    ))}
  </ul>
);
